import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertTriangle } from 'lucide-react'

export const Route = createFileRoute('/_protected/admin/serie')({
  component: SeriePage,
})

type Job = {
  id: string
  dryRun: boolean
  estado: 'corriendo' | 'ok' | 'error'
  iniciado: number
  terminado?: number
  exitCode?: number
  progreso: { hechas: number; total: number } | null
  lineas: string[]
}

function SeriePage() {
  const [jobId, setJobId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const logRef = React.useRef<HTMLPreElement>(null)

  // Al entrar, retoma un trabajo que ya estuviera corriendo (p. ej. tras recargar la página).
  const { data: estado } = useQuery({
    queryKey: ['backfill-estado'],
    queryFn: async () => {
      const res = await fetch('/api/admin/backfill', { credentials: 'include' })
      return res.json() as Promise<{ enCurso: string | null }>
    },
    refetchInterval: (q) => (jobId ? false : q.state.data?.enCurso ? 2000 : false),
  })

  React.useEffect(() => {
    if (!jobId && estado?.enCurso) setJobId(estado.enCurso)
  }, [estado?.enCurso, jobId])

  const { data: job } = useQuery({
    queryKey: ['backfill', jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const res = await fetch(`/api/admin/backfill/${jobId}`, { credentials: 'include' })
      return res.json() as Promise<Job>
    },
    refetchInterval: (q) => (q.state.data?.estado === 'corriendo' ? 1500 : false),
  })

  React.useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [job?.lineas.length])

  async function lanzar(dryRun: boolean) {
    setError(null)
    if (!dryRun && !confirm(
      'Vas a ejecutar la reposición en firme.\n\n' +
      'Inserta millones de muestras y tarda varios minutos. El script respalda el catálogo ' +
      'de zonas y deja los rangos insertados en data/backfill-rangos.json para poder revertir.\n\n' +
      '¿Continuar?',
    )) return

    const res = await fetch('/api/admin/backfill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ dryRun }),
    })
    const data = await res.json()
    if (!res.ok) { setError(data.error ?? `Error ${res.status}`); return }
    setJobId(data.jobId)
  }

  const corriendo = job?.estado === 'corriendo'
  const pct = job?.progreso ? Math.round((100 * job.progreso.hechas) / job.progreso.total) : null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Reposición de la serie de ping</CardTitle>
          <p className="text-sm text-muted-foreground">
            Alinea el arranque de cada serie con la fecha de instalación y rellena las muestras que
            faltan. No modifica ninguna fila existente: sólo inserta por delante.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Ejecuta primero la simulación</p>
              <p className="text-amber-900/80">
                Muestra exactamente qué zonas se rellenarían y cuántas muestras, sin escribir nada.
                La ejecución en firme tarda varios minutos.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" disabled={corriendo} onClick={() => lanzar(true)}>
              Simular
            </Button>
            <Button disabled={corriendo} onClick={() => lanzar(false)}>
              Ejecutar en firme
            </Button>
          </div>

          {error && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>
          )}

          {job && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <EstadoPill estado={job.estado} />
                <span className="text-muted-foreground">
                  {job.dryRun ? 'Simulación' : 'Ejecución en firme'}
                </span>
                {job.progreso && (
                  <span className="text-muted-foreground">
                    {job.progreso.hechas} / {job.progreso.total} zonas
                  </span>
                )}
                {job.estado === 'error' && (
                  <span className="text-red-600">código de salida {job.exitCode}</span>
                )}
              </div>

              {pct !== null && corriendo && (
                <div className="h-2 w-full bg-slate-200 rounded overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}

              <pre
                ref={logRef}
                className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 max-h-96 overflow-auto whitespace-pre-wrap"
              >
                {job.lineas.join('\n') || 'Iniciando…'}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function EstadoPill({ estado }: { estado: Job['estado'] }) {
  const map = {
    corriendo: ['En curso', 'bg-blue-100 text-blue-800'],
    ok: ['Terminado', 'bg-green-100 text-green-800'],
    error: ['Con errores', 'bg-red-100 text-red-800'],
  } as const
  const [label, cls] = map[estado]
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{label}</span>
}
